'use client';

import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/lib/utils';
import {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useInView } from 'react-intersection-observer';

const subscribeNever = () => () => {};

const TRUSTPILOT_SRC =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
let trustpilotReady: Promise<void> | null = null;

// Injects the Trustpilot bootstrap once per page session, shared by every TrustBox.
function loadTrustpilot() {
  if (!trustpilotReady) {
    trustpilotReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TRUSTPILOT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Allows a retry on the next widget
        trustpilotReady = null;
        script.remove();
        reject();
      };
      document.head.appendChild(script);
    });
  }
  return trustpilotReady;
}

export default function TrustBox() {
  const ref = useRef<HTMLDivElement | null>(null);
  // The bootstrap is only requested when a widget gets close to the viewport, so pages
  // without a TrustBox never download it.
  const { ref: inViewRef, inView } = useInView({
    rootMargin: '300px',
    triggerOnce: true,
  });
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );
  const lang = useLocale();
  // The Trustpilot bootstrap fills every .trustpilot-widget it finds. Inside a streamed Suspense
  // boundary it can run before React hydrates the widget, and the injected iframe breaks
  // hydration (#418). The class is added only after hydration, so the script never sees it first.
  const hydrated = useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );

  const localeMap: Record<string, string> = {
    en: 'en-GB',
    it: 'it-IT',
    de: 'de-DE',
    fr: 'fr-FR',
  };

  useEffect(() => {
    if (!hydrated || !inView) return;

    let cancelled = false;

    loadTrustpilot()
      .then(() => {
        const trustpilot = (window as any).Trustpilot;

        // On first load the bootstrap already fills every .trustpilot-widget in the DOM; without
        // forceReload this call skips a filled widget and only renders remounted ones.
        if (!cancelled && trustpilot && ref.current) {
          trustpilot.loadFromElement(ref.current);
        }
      })
      // The widget is decorative: if the script fails, the plain review link stays
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [hydrated, inView]);

  return (
    <div
      ref={setRefs}
      className={cn(
        // min-h reserves the 20px widget iframe plus the mobile padding, so it does not shift the
        // title when it loads
        'pt-4 pb-8 desktop:py-0 min-h-[68px] desktop:min-h-0 font-medium desktop:max-w-[230px]',
        {
          'trustpilot-widget': hydrated,
          'desktop:max-w-[235px]': lang === 'de',
        },
      )}
      data-locale={localeMap[lang]}
      data-template-id="5419b637fa0340045cd0c936"
      data-businessunit-id="60f03f2970d674000116f325"
      data-style-height="20px"
      data-style-width="100%">
      <a
        href={`https://${lang === 'en' ? 'uk' : lang}.trustpilot.com/review/www.villatravellers.com`}
        target="_blank"
        rel="noopener"></a>
    </div>
  );
}
